/**
 * PVS Shipping Protection
 * In-house replacement for the Insureful checkout add-on (saves the monthly fee).
 *
 * The fee tier is calculated server-side in Liquid (snippets/pvs-shipping-protection.liquid):
 * 5.5% of the cart subtotal, floored to whole dollars, clamped to the $2..$100 variants of the
 * existing "insurance-percentage-plan" product. This script only mutates the cart line item and
 * lets the theme's own section-rendering refresh the UI. Because the widget lives inside the
 * re-rendered cart summary, every refresh produces a fresh element with up-to-date data
 * attributes, so there is no client-side state to keep in sync.
 *
 * Opt-in: unticked by default, never auto-added (25 Oct 2026). A duplicate line or a quantity above 1
 * is collapsed back to one line (see the duplicate branch in connectedCallback and add()).
 *
 * Globals provided by the theme: theme.routes.cartAdd, theme.routes.cartChange.
 */
if (!customElements.get('pvs-shipping-protection')) {
  class PvsShippingProtection extends HTMLElement {
    connectedCallback() {
      const enabled = this.dataset.enabled === 'true';
      const suppressed = this.dataset.suppressed === 'true';
      const subtotal = this.dataset.subtotal;
      const currentKey = this.dataset.currentKey;
      const currentPrice = this.dataset.currentPrice;
      const desiredPrice = this.dataset.desiredPrice;
      const desiredVariant = this.dataset.desiredVariant;

      // Bind the toggle FIRST, before any of the automatic branches below can return.
      // Those branches used to sit above this line, so whenever one of them fired the
      // element returned with no change listener attached and the toggle was dead: the
      // customer clicked it and nothing happened, with no error to show for it.
      const toggle = this.querySelector('.pvs-ship-prot__toggle');
      if (toggle) toggle.addEventListener('change', this.onToggle.bind(this));

      // More than one protection line, or quantity above 1: collapse to a single line.
      if (enabled && this.dataset.protLines && Number(this.dataset.protLines) > 1 && currentKey) {
        this.run(async () => {
          const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
          const lines = (cart.items || []).filter((i) => /^insurance-percentage-plan/.test(i.handle));
          for (const extra of lines.slice(1)) await this.change(extra.key, 0);
          if (lines[0] && lines[0].quantity > 1) await this.change(lines[0].key, 1);
        });
        return;
      }

      // Cart has protection but no longer has any real products: drop it.
      if (enabled && subtotal === '0' && currentKey) {
        this.run(() => this.remove(currentKey));
        return;
      }

      // Cart moved into the band where the fee itself would tip the order over the free-shipping
      // threshold (see the snippet). Drop protection before the fee-band swap below can re-add it.
      if (enabled && suppressed && currentKey) {
        this.run(() => this.remove(currentKey));
        return;
      }

      // Cart subtotal moved into a different fee band: swap to the correct tier automatically.
      if (enabled && desiredVariant && currentPrice !== desiredPrice) {
        this.run(async () => {
          if (currentKey) await this.change(currentKey, 0);
          await this.add(desiredVariant);
        });
        return;
      }

      // OPT-IN since 25 Oct 2026 (owner approved the buyer walkthrough fixes): protection is
      // never added automatically. It starts unticked and only the customer's own tick adds it.
      // The old default-on branch lived here; with the drawer and the cart page both rendering a
      // widget it could also add the line twice ($136 seen on a $68 fee).
    }

    /**
     * Whether the customer turned protection off earlier this session.
     * Session-scoped so each new cart defaults back to on, but we never re-add
     * within a session after they've explicitly removed it.
     * @returns {boolean}
     */
    optedOut() {
      try { return sessionStorage.getItem('pvsProtOptOut') === '1'; } catch (e) { return false; }
    }

    /**
     * Records (or clears) the customer's opt-out preference for this session.
     * @param {boolean} value - true to remember the opt-out, false to clear it.
     */
    setOptOut(value) {
      try {
        if (value) sessionStorage.setItem('pvsProtOptOut', '1');
        else sessionStorage.removeItem('pvsProtOptOut');
      } catch (e) { /* storage unavailable, default-on still works */ }
    }

    /**
     * Handles the customer ticking / unticking the protection toggle.
     * @param {Event} evt - Change event.
     */
    onToggle(evt) {
      const on = evt.target.checked;
      this.setOptOut(!on); // remember an explicit opt-out so default-on doesn't re-add it
      this.run(
        () => (on ? this.add(this.dataset.desiredVariant) : this.remove(this.dataset.currentKey)),
        () => { evt.target.checked = !on; this.setOptOut(on); }, // revert toggle + preference if it fails
        { queue: true } // a customer's click is never dropped, even mid-refresh
      );
    }

    /**
     * Runs a cart mutation, then refreshes the cart.
     *
     * Mutations are serialised on a global promise chain so the drawer and page instances
     * can never race each other. An AUTOMATIC action is skipped while another mutation is in
     * flight, because the section re-render that follows it re-evaluates server-side and will
     * retry if the cart still needs the change. A QUEUED action (a customer's click) waits its
     * turn instead of being dropped: dropping it left the toggle showing "on" with nothing
     * added to the cart, which is how an order reached checkout with no protection line.
     *
     * @param {Function} action - Async function that performs the cart mutation.
     * @param {Function} [onError] - Optional callback run if the mutation throws.
     * @param {{queue: boolean}} [options] - queue: true to wait for the lock rather than skip.
     * @returns {Promise}
     */
    run(action, onError, options) {
      const opts = options || {};
      if (!opts.queue && window.__pvsProtBusy) return Promise.resolve();
      window.__pvsProtBusy = true;
      const previous = window.__pvsProtQueue || Promise.resolve();
      const task = previous
        .catch(() => {})
        .then(async () => {
          this.classList.add('pvs-ship-prot--busy');
          try {
            await action();
          } catch (error) {
            console.log('[pvs-shipping-protection]', error); // eslint-disable-line
            if (onError) onError();
            this.classList.remove('pvs-ship-prot--busy');
            return;
          }
          // Refreshing is a separate concern from mutating. Keeping it inside the try above
          // meant a failed refresh ran onError and flipped the toggle back, telling the
          // customer their change had failed when the cart had in fact already accepted it.
          try {
            this.refreshCart();
          } catch (error) {
            console.log('[pvs-shipping-protection] refresh', error); // eslint-disable-line
            this.classList.remove('pvs-ship-prot--busy');
          }
        })
        .then(() => {
          // Release shortly after, by which point the section refresh has replaced this
          // element. Only the tail of the chain clears the lock, so a click that queued
          // behind this one keeps it held rather than having it pulled out from under it.
          setTimeout(() => {
            if (window.__pvsProtQueue === task) window.__pvsProtBusy = false;
          }, 600);
        });
      window.__pvsProtQueue = task;
      return task;
    }

    /**
     * Adds the protection variant to the cart.
     * @param {string} variantId - Variant id to add.
     * @returns {Promise}
     */
    async add(variantId) {
      // Never a second protection line: if one is already in the cart, leave it to the
      // tier logic rather than stacking another on top.
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const existing = (cart.items || []).filter((i) => /^insurance-percentage-plan/.test(i.handle));
        if (existing.length) {
          for (const extra of existing.slice(1)) await this.change(extra.key, 0);
          if (existing[0].quantity > 1) await this.change(existing[0].key, 1);
          if (String(existing[0].variant_id) === String(variantId)) return cart;
          await this.change(existing[0].key, 0);
        }
      } catch (e) { /* fall through to a plain add */ }
      return fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _pvs_protection: 'true' } }]
        })
      }).then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
    }

    /**
     * Removes the protection line item from the cart.
     * @param {string} key - Line item key.
     * @returns {Promise}
     */
    remove(key) {
      return this.change(key, 0);
    }

    /**
     * Changes the quantity of a line item by its key.
     * @param {string} key - Line item key.
     * @param {number} quantity - New quantity.
     * @returns {Promise}
     */
    change(key, quantity) {
      return fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      }).then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
    }

    /**
     * Asks the theme to re-render the cart, using whichever mechanism fits the current context.
     */
    refreshCart() {
      // Ask the widget what it sits inside rather than requiring one specific id to exist in
      // the document. The old check needed #cart-drawer, so on any template that renders the
      // drawer under a different element the widget fell through to a full page reload.
      if (this.closest('cart-drawer, #cart-drawer, [data-cart-drawer], .drawer--cart')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        return;
      }
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        cartItems.refresh();
      } else {
        window.location.reload();
      }
    }
  }

  customElements.define('pvs-shipping-protection', PvsShippingProtection);
}
